import { useEffect, useRef, useState } from 'react'
import { MapPin } from 'lucide-react'
import { Input } from './Input'
import { geocodeService, AddressSuggestion } from '../services/api'
import { logger } from '../utils/logger'

interface AddressAutocompleteProps {
  id: string
  label: string
  placeholder?: string
  value: string
  country: string
  error?: string
  onChange: (value: string) => void
  onBlur?: () => void
  onSelect: (suggestion: AddressSuggestion) => void
}

const DEBOUNCE_MS = 300
const MIN_CHARS = 3

/**
 * Street input with OSM-backed suggestions (accessible combobox).
 * Degrades silently to a plain input when the geocoder is unavailable —
 * the patient can always type freely and ignore suggestions.
 */
export function AddressAutocomplete({
  id, label, placeholder, value, country, error, onChange, onBlur, onSelect,
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([])
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(-1)
  const abortRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const skipNextFetch = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (value.trim().length < MIN_CHARS) {
      setSuggestions([])
      setOpen(false)
      return
    }

    debounceRef.current = setTimeout(async () => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      try {
        const result = await geocodeService.autocomplete(value.trim(), country, controller.signal)
        if (controller.signal.aborted) return
        setSuggestions(result.suggestions)
        setHighlighted(-1)
        setOpen(result.suggestions.length > 0)
      } catch (err) {
        if (!controller.signal.aborted) {
          logger.warn('[AddressAutocomplete] lookup failed:', err)
          setSuggestions([])
          setOpen(false)
        }
      }
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, country])

  // Cleanup on unmount
  useEffect(() => () => abortRef.current?.abort(), [])

  const select = (s: AddressSuggestion) => {
    skipNextFetch.current = true
    setOpen(false)
    setSuggestions([])
    onSelect(s)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || suggestions.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted(h => Math.min(h + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted(h => Math.max(h - 1, 0))
    } else if (e.key === 'Enter' && highlighted >= 0) {
      e.preventDefault()
      select(suggestions[highlighted])
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        id={id}
        type="text"
        label={label}
        placeholder={placeholder}
        value={value}
        error={error}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-autocomplete="list"
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          // Delay so a click on a suggestion still registers
          setTimeout(() => setOpen(false), 150)
          onBlur?.()
        }}
        onFocus={() => { if (suggestions.length > 0) setOpen(true) }}
      />
      {open && (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          className="absolute z-30 mt-1 w-full bg-white border border-border rounded-card shadow-lg max-h-64 overflow-y-auto"
        >
          {suggestions.map((s, i) => (
            <li
              key={`${s.lat}-${s.lon}-${i}`}
              role="option"
              aria-selected={i === highlighted}
              className={`flex items-start gap-2 px-3 py-2 cursor-pointer text-body ${
                i === highlighted ? 'bg-primary/10' : 'hover:bg-muted/60'
              }`}
              onMouseDown={(e) => { e.preventDefault(); select(s) }}
              onMouseEnter={() => setHighlighted(i)}
            >
              <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              <span>{s.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
